import { AmberApi } from 'app/core/models/api-helpers';
import { Component, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject, BehaviorSubject } from 'rxjs';

import { RxComponent } from 'app/core/components/common/rx.component';
import { GlobalValidators } from 'app/core/components/common/validators';
import { SmartModalService } from 'app/core/services/smart-modal.service';
import { ClassesService } from 'app/solar/common/services/classes.service';

@Component({
    selector: 'itpx-add-instructor',
    templateUrl: './add-instructor.component.html',
    styleUrls: ['./add-instructor.component.css']
})
export class AddInstructorComponent extends RxComponent {

    public state = State;

    private emailForm: FormGroup;
    private createForm: FormGroup;
    private promoteForm: FormGroup;

    @Input()
    emitResult: boolean;

    // Inputs
    private backClick = new Subject<any>();
    private formSubmit = new Subject<any>();

    // Outputs
    private step = new BehaviorSubject<State>(State.Email);
    @Output()
    private result = new EventEmitter();

    constructor(
        private fb: FormBuilder,
        private modalService: SmartModalService,
        private service: ClassesService
    ) {
        super();
    }

    ngOnInit() {

        this.emailForm = this.fb.group({
            email: [null, [Validators.required, GlobalValidators.email]]
        });

        this.createForm = this.fb.group({
            username: [null],
            type: ['instructor'],
            firstName: [null, [Validators.required, Validators.minLength(2)]],
            lastName: [null, [Validators.required, Validators.minLength(2)]]
        });

        this.promoteForm = this.fb.group({
            id: [null]
        });

        let formResult = this.formSubmit
            .withLatestFrom(this.step, (data, step) => ({ step: step, form: data }))
            .flatMap(data => this.handleFormSubmit(data.step, data.form)
                .map(res => ({ data: res, step: data.step }))
                .neverError()
            )
            .shareReplay();

        Observable.merge(
                formResult.filter(res => res.data.createNew).mapTo(State.Create),
                formResult.filter(res => res.data.promote).mapTo(State.Promote),
                this.backClick.mapTo(State.Email)
            )
            .bindToLifecycle(this)
            .subscribe(state => this.step.next(state));

        formResult
            .bindToLifecycle(this)
            .subscribe(res => {
                switch (res.step) {
                    case State.Email:
                        if (res.data.alreadyInstructor) {
                            this.sendResult(res.data.user.id);
                        }
                        else if (res.data.createNew) {
                            this.createForm.controls.username.setValue(this.emailForm.value.email);
                        }
                        else if (res.data.promote) {
                            this.promoteForm.controls.id.setValue(res.data.user.id);
                        }

                        break;
                    case State.Promote:
                        this.sendResult(res.data.id);
                        break;
                    case State.Create:
                        this.sendResult(res.data.id);
                        break;
                }
            });
    }

    protected getFormSubmitQuery(data: any): Observable<any> {
        return this.step
            .flatMap(step => (step == 1 ? this.validateInstructorEmail(data.email) : this.createInstructor(data))
                .map(data => ({ data: data, step: step }))
            );
    }

    private handleFormSubmit(step: State, form: any): Observable<any> {
        switch (step) {
            case State.Email:
                return this.validateInstructorEmail(form.email);
            case State.Promote:
                return this.promoteToInstructor(form);
            case State.Create:
                return this.createInstructor(form);
            default:
                return Observable.throw('Unknown state');
        }
    }

    private validateInstructorEmail(email: string): Observable<any> {
        return this.service.getInstructor(email, true)
            .map(body => body.data)
            // Treat 404 as OK => email is available
            .catch(err => err.status == 404 ? Observable.of(null) : Observable.throw(err))
            .flatMap(user => {
                if (user) {
                    if (user.type == 'instructor') {
                        return this.service.toggleInstructor([user.id], '1')
                            .neverError()
                            .map(res => ({ user: user, alreadyInstructor: true}))
                    } else {
                        return Observable.of<any>({ user: user, promote: true });
                    }
                }

                return Observable.of<any>({ createNew: true });
            });
    }

    private createInstructor(data: any): Observable<any> {
        return this.service.addInstructor(data)
            .extract(AmberApi.Data)
            .neverError();
    }

    private promoteToInstructor(data: any): Observable<any> {
        return this.service.promoteToInstructor(data.id)
            .extract(AmberApi.Data)
            .neverError();
    }

    private onSubmit({ value, valid }: { value: any, valid: boolean }) {
        this.formSubmit.next(value);
    }

    private onCancel() {
        this.sendResult(null);
    }

    private sendResult(result) {
        if (this.emitResult) {
            this.result.emit(result);
        } else {
            this.modalService.sendResult(result);
        }
    }
}

export enum State {
    Email = 1, Promote = 2, Create = 3
}


// WEBPACK FOOTER //
// ./src/app/solar/common/add-instructor/add-instructor.component.ts